import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Button,
  CircularProgress,
  FormGroup,
  Input,
  InputAdornment,
  Typography,
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useRequestPasswordReset } from '../hooks/useRequestPasswordReset';

const schema = yup
  .object({
    email: yup
      .string()
      .trim()
      .lowercase()
      .email('You must provide a valid email address')
      .required('Email is required'),
  })
  .required();

type ForgotPasswordFormValues = yup.InferType<typeof schema>;

function ForgotPasswordForm(): ReactElement {
  const navigate = useNavigate();
  const { mutate: requestReset, isPending, isSuccess } = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({ resolver: yupResolver(schema) });

  function onSubmit(data: ForgotPasswordFormValues): void {
    if (isPending) return;

    requestReset(data.email);
  }

  if (isSuccess) {
    return (
      <Box className="m-auto w-2/3 max-w-md text-center">
        <Typography variant="h5" component="h1" className="mb-16">
          Check your email
        </Typography>
        <Typography className="mb-16">
          If an account exists for that email, we&apos;ve sent a link to reset your password.
        </Typography>
        <Button className="w-full" variant="contained" onClick={() => navigate('/login')}>
          Back to login
        </Button>
      </Box>
    );
  }

  return (
    <Box className="m-auto w-2/3 max-w-md">
      <Typography variant="h5" component="h1" className="text-center mb-16">
        Reset your password
      </Typography>
      <Typography className="text-center mb-16">
        Enter your email address and we&apos;ll send you a link to reset your password.
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <div className="mb-16">
            <Input
              className="w-full"
              color="success"
              placeholder="Email"
              {...(errors.email?.message && { error: true })}
              {...register('email')}
              startAdornment={
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              }
            />
            {errors.email?.message && (
              <Typography variant="caption" color="error">
                {errors.email?.message}
              </Typography>
            )}
          </div>

          <Box className="flex flex-col vtwo:gap-4">
            <Button className="w-full" variant="contained" type="submit" disabled={isPending}>
              {isPending && <CircularProgress size={20} className="vtwo:mr-2.5" />}Send reset link
            </Button>
            <Button className="w-full" type="button" onClick={() => navigate('/login')}>
              Back to login
            </Button>
          </Box>
        </FormGroup>
      </form>
    </Box>
  );
}

export default ForgotPasswordForm;
